import { useLingui } from "@lingui/react";
import type { CarAd, CaravanAd, MotorbikeAd } from "@mobility-item-page/types";
import { formatNumber } from "@mobility-item-page/utilities";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import FieldValuePair from "../FieldValuePair.tsx";
import type { SpecProps } from "./types.ts";

export const Warranty = ({ warranty }: SpecProps<"warranty", CarAd | MotorbikeAd | CaravanAd>) => {
  const { i18n } = useLingui();

  const warrantyTypeField = i18n.t({
    id: "itemPageLayout.keyInfo.warranty.type",
    message: "Warranty",
    comment: "Field for 'warranty type' in ad specification",
  });

  if (typeof warranty === "boolean") {
    return <FieldValuePair field={warrantyTypeField} value={warranty} />;
  }

  if (!warranty) {
    return null;
  }

  return (
    <>
      <FieldValuePair field={warrantyTypeField} value={warranty.warranty_type} />
      {!!warranty.duration && <Duration duration={warranty.duration} />}
      {!!warranty.distance && <Distance distance={warranty.distance} />}
    </>
  );
};

const Duration = ({ duration }: { duration: number }) => {
  const { i18n } = useLingui();
  const { locale } = useAppConfig();
  const warrantyDurationField = i18n.t({
    id: "itemPageLayout.keyInfo.warranty.duration",
    message: "Warranty length",
    comment: "Field for 'warranty duration' in ad specification",
  });

  const value = i18n.t({
    id: "itemPageLayout.keyInfo.warranty.duration.postfix",
    message: "{duration, plural, one {{formattedDuration} month} other {{formattedDuration} months}}",
    comment: "Warranty duration postfix in ad specification (singular/plural)",
    values: {
      duration,
      formattedDuration: formatNumber(duration, locale),
    },
  });

  return <FieldValuePair field={warrantyDurationField} value={value} />;
};

const Distance = ({ distance }: { distance: number }) => {
  const { i18n } = useLingui();
  const { locale } = useAppConfig();

  const field = i18n.t({
    id: "itemPageLayout.keyInfo.warranty.distance",
    message: "Warranty distance",
    comment: "Field for 'warranty distance' in ad specification",
  });

  const value = i18n.t({
    id: "itemPageLayout.common.distance.kilometer.postfix",
    message: "{distance} km",
    comment: "Distance incl. postfix",
    values: { distance: formatNumber(distance, locale) },
  });

  return <FieldValuePair field={field} value={value} />;
};
